<template>
  <div class="diff-view">
    <div class="diff-summary">
      <span class="labels">
        <span class="lbl del-lbl">− {{ leftLabel }}</span>
        <span class="lbl add-lbl">+ {{ rightLabel }}</span>
      </span>
      <span class="stats" v-if="!diff.identical">
        <span class="added">+{{ diff.stats.added }}</span>
        <span class="removed">−{{ diff.stats.removed }}</span>
      </span>
      <span class="mode-note" v-if="isNarrow && mode === 'split'">unified (narrow screen)</span>
    </div>

    <p v-if="diff.identical" class="identical">{{ emptyMessage }}</p>

    <template v-else>
      <p v-if="truncated" class="truncated">
        ⚠ Large diff: showing the first {{ visibleHunks.length }} of
        {{ diff.hunks.length }} sections. Switch to raw JSON or narrow the comparison to see the rest.
      </p>

      <!-- Unified -->
      <table v-if="effectiveMode === 'unified'" class="diff-table unified">
        <!-- Column widths must come from <col>. Under `table-layout: fixed`
             only the first row sizes the columns, and that row is the hunk
             header's single colspan cell, so per-cell widths are ignored. -->
        <colgroup>
          <col class="col-ln" />
          <col class="col-ln" />
          <col />
        </colgroup>
        <tbody v-for="(hunk, hi) in visibleHunks" :key="'u' + hi">
          <tr class="hunk-header">
            <td colspan="3">{{ hunk.header }}</td>
          </tr>
          <tr v-for="(row, ri) in hunk.rows" :key="'u' + hi + '-' + ri" :class="'row-' + row.type">
            <td class="ln">{{ row.oldNo }}</td>
            <td class="ln">{{ row.newNo }}</td>
            <td class="code">
              <span class="marker">{{ marker(row) }}</span
              ><span class="text"><template v-if="row.segments"><span
                    v-for="(seg, si) in row.segments"
                    :key="si"
                    :class="{ 'seg-changed': seg.changed }"
                    >{{ seg.value }}</span
                  ></template><template v-else>{{ row.text }}</template></span>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Split -->
      <table v-else class="diff-table split">
        <colgroup>
          <col class="col-ln" />
          <col class="col-code" />
          <col class="col-ln" />
          <col class="col-code" />
        </colgroup>
        <tbody v-for="(hunk, hi) in visibleSplitHunks" :key="'s' + hi">
          <tr class="hunk-header">
            <td colspan="4">{{ hunk.header }}</td>
          </tr>
          <tr v-for="(pair, ri) in hunk.pairs" :key="'s' + hi + '-' + ri">
            <td class="ln">{{ pair.left ? pair.left.oldNo : "" }}</td>
            <td class="code" :class="cellClass(pair.left, 'left')">
              <span class="text" v-if="pair.left"><template v-if="pair.left.segments"><span
                    v-for="(seg, si) in pair.left.segments"
                    :key="si"
                    :class="{ 'seg-changed': seg.changed }"
                    >{{ seg.value }}</span
                  ></template><template v-else>{{ pair.left.text }}</template></span>
            </td>
            <td class="ln">{{ pair.right ? pair.right.newNo : "" }}</td>
            <td class="code" :class="cellClass(pair.right, 'right')">
              <span class="text" v-if="pair.right"><template v-if="pair.right.segments"><span
                    v-for="(seg, si) in pair.right.segments"
                    :key="si"
                    :class="{ 'seg-changed': seg.changed }"
                    >{{ seg.value }}</span
                  ></template><template v-else>{{ pair.right.text }}</template></span>
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </div>
</template>

<script>
import { buildHunks, toSplitRows } from "../utils/model_diff";

// Rendering every row of a very large diff locks the page up. Past this many
// rows we drop whole trailing hunks and say so.
const MAX_ROWS = 3000;
const NARROW_QUERY = "(max-width: 768px)";

export default {
  name: "ModelDiffView",
  props: {
    leftText: { type: String, default: "" },
    rightText: { type: String, default: "" },
    leftLabel: { type: String, default: "left" },
    rightLabel: { type: String, default: "right" },
    // "unified" | "split". Split is forced to unified on narrow screens.
    mode: { type: String, default: "unified" },
    context: { type: Number, default: 3 },
    emptyMessage: { type: String, default: "No differences." },
  },
  data() {
    return {
      isNarrow: false,
      mediaQuery: null,
    };
  },
  computed: {
    diff() {
      return buildHunks(this.leftText, this.rightText, { context: this.context });
    },
    effectiveMode() {
      return this.isNarrow ? "unified" : this.mode;
    },
    // Trim trailing hunks once the row budget is spent.
    visibleHunks() {
      const out = [];
      let rows = 0;
      for (const hunk of this.diff.hunks) {
        if (rows && rows + hunk.rows.length > MAX_ROWS) break;
        out.push(hunk);
        rows += hunk.rows.length;
        if (rows >= MAX_ROWS) break;
      }
      return out;
    },
    visibleSplitHunks() {
      return this.visibleHunks.map((hunk) => ({
        header: hunk.header,
        pairs: toSplitRows(hunk),
      }));
    },
    truncated() {
      return this.visibleHunks.length < this.diff.hunks.length;
    },
  },
  mounted() {
    this.mediaQuery = window.matchMedia(NARROW_QUERY);
    this.isNarrow = this.mediaQuery.matches;
    this.onMediaChange = (e) => {
      this.isNarrow = e.matches;
    };
    this.mediaQuery.addEventListener("change", this.onMediaChange);
  },
  beforeUnmount() {
    if (this.mediaQuery) this.mediaQuery.removeEventListener("change", this.onMediaChange);
  },
  methods: {
    marker(row) {
      if (row.type === "add") return "+";
      if (row.type === "del") return "−";
      return " ";
    },
    cellClass(row, side) {
      if (!row) return "cell-empty";
      if (row.type === "context") return "cell-context";
      return side === "left" ? "cell-del" : "cell-add";
    },
  },
};
</script>

<style scoped>
.diff-view {
  border: 1px solid #d6d6d6;
  border-radius: 6px;
  background: #fff;
  overflow: hidden;
}
.diff-summary {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 8px 12px;
  background: #f3f4f6;
  border-bottom: 1px solid #d6d6d6;
  font-size: 0.85em;
}
.labels {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  min-width: 0;
}
.lbl {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  overflow-wrap: anywhere;
}
.del-lbl {
  color: #a4262c;
}
.add-lbl {
  color: #1a7f37;
}
.stats {
  margin-left: auto;
  display: flex;
  gap: 8px;
  font-weight: 600;
}
.stats .added {
  color: #1a7f37;
}
.stats .removed {
  color: #a4262c;
}
.mode-note {
  color: #666;
  font-style: italic;
}
.identical {
  margin: 0;
  padding: 14px 12px;
  color: #555;
  font-size: 0.9em;
}
.truncated {
  margin: 0;
  padding: 8px 12px;
  background: #fff8e1;
  border-bottom: 1px solid #e6d9a8;
  color: #7a5c00;
  font-size: 0.82em;
}

.diff-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.8em;
  line-height: 1.5;
}
.hunk-header td {
  background: #eef2f7;
  color: #5a6b7f;
  padding: 3px 8px;
  border-top: 1px solid #dde3ea;
  border-bottom: 1px solid #dde3ea;
}
/* Gutters are sized on <col> above, not here; line numbers never exceed 4
   digits at these text sizes, so the content columns get everything else. */
.diff-table col.col-ln {
  width: 34px;
}
.split col.col-code {
  width: calc(50% - 34px);
}

.ln {
  padding: 0 4px;
  text-align: right;
  color: #98a2b3;
  background: #fafbfc;
  vertical-align: top;
  user-select: none;
  border-right: 1px solid #eef0f2;
}
.code {
  padding: 0 8px 0 0;
  vertical-align: top;
  /* Prompts are long: wrap instead of scrolling the page sideways. */
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.code .marker {
  display: inline-block;
  width: 1.1em;
  color: #98a2b3;
  user-select: none;
}
/* A blank line has no content, so it would generate no line box and collapse to
   zero height, knocking the two sides of a split diff out of alignment. The
   zero-width space forces a line box without being selectable as content. */
.code .text::after {
  content: "\200b";
}

.unified .row-add {
  background: #e6ffed;
}
.unified .row-del {
  background: #ffeef0;
}
.unified .row-add .marker {
  color: #1a7f37;
}
.unified .row-del .marker {
  color: #a4262c;
}

.split .code {
  border-left: 1px solid #eef0f2;
  padding-left: 8px;
}
.cell-add {
  background: #e6ffed;
}
.cell-del {
  background: #ffeef0;
}
.cell-empty {
  background: #fafbfc;
}

.seg-changed {
  border-radius: 2px;
  padding: 0 1px;
}
.row-add .seg-changed,
.cell-add .seg-changed {
  background: #a6f0b8;
}
.row-del .seg-changed,
.cell-del .seg-changed {
  background: #f7bfc4;
}
</style>
